import { HumanizeDateTime } from "@/lib/dates";
import { useLocation } from "react-router";
import { useOrgRoutes, useRoutes } from "@/routes";
import {
  IdentityPanel,
  IdentityPanelEmpty,
  IdentityPanelRow,
} from "./IdentityPanel";
import { identityHandoffs } from "./identityHandoffs";
import { useIdentityOutlet } from "./identityRoute";
import { DailyActivityChart } from "@/components/chart/DailyActivityChart";
import { IdentitySection } from "./IdentitySection";
import { sectionMeta } from "./sectionMeta";
import {
  retryFailed,
  useCanReadOthersChats,
  useIdentityAuditLogs,
  useIdentityChats,
  useIdentityWindow,
  useIsSelf,
} from "./useIdentityQueries";

// The lists are a recent sample, not an archive: the handoff owns the full
// history and a 20-row list only made this tab a longer Overview.
const RECENT_ROWS = 6;

/**
 * What a panel says when a refresh failed over rows it already has: the rows
 * stay, because they were true when they landed, and the line says they may
 * now be stale rather than replacing them with an error.
 */
function RefreshFailed({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <span>
      Could not refresh — showing the last rows loaded.{" "}
      <button
        type="button"
        onClick={onRetry}
        className="hover:text-foreground focus-visible:ring-ring rounded-xs underline underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
      >
        Try again
      </button>
    </span>
  );
}

export default function IdentityActivity(): JSX.Element {
  const { identity } = useIdentityOutlet();
  const { from, to } = useIdentityWindow();
  const location = useLocation();
  const routes = useRoutes();
  const orgRoutes = useOrgRoutes();
  // No handoff on this page filters by principal, so the member list this
  // would otherwise fetch is not worth the request.
  const handoffs = identityHandoffs(
    identity,
    routes,
    orgRoutes,
    undefined,
    new URLSearchParams(location.search),
  );

  const auditQuery = useIdentityAuditLogs(identity, from, to);
  const chatsQuery = useIdentityChats(identity, from, to, 20);
  const hasChatRead = useCanReadOthersChats();
  const isSelf = useIsSelf(identity);
  const canReadOthersChats = hasChatRead || isSelf;

  const logs = auditQuery.data?.result.logs ?? [];
  const chats = chatsQuery.data?.chats ?? [];
  // isLoading rather than isPending: the chat query is held behind `enabled`
  // for a viewer without chat:read, and that is a refusal to fetch, not a wait.
  const activityLoading = auditQuery.isLoading || chatsQuery.isLoading;
  // The chart draws both series as one shape, so a failed half would read as
  // a quiet fortnight rather than as data that never arrived.
  const activityFailed = auditQuery.isError || chatsQuery.isError;
  const retryActivity = retryFailed(auditQuery, chatsQuery);

  const logDates = logs.map((log) => new Date(log.createdAt));
  const chatDates = chats
    .map((chat) => chat.lastMessageTimestamp)
    .filter((ts): ts is Date => Boolean(ts))
    .map((ts) => new Date(ts));

  // A failed refresh keeps the rows it already has: those were really
  // returned, and blanking them throws away the only true thing the panel
  // holds. Only a failure with nothing behind it blocks the body.
  const auditBlocked = auditQuery.isError && logs.length === 0;
  const chatsBlocked = chatsQuery.isError && chats.length === 0;
  const chartEmpty = logDates.length === 0 && chatDates.length === 0;

  return (
    <IdentitySection
      title="Activity"
      meta={sectionMeta([
        { count: logs.length, singular: "change" },
        { count: chats.length, singular: "session" },
      ])}
    >
      {/* Overview already shows the most recent handful of both lists. What
          this tab can add is the shape of the window: two people with
          identical row lists can work in daily drips or in two long bursts,
          and only the columns tell them apart. */}
      {(activityLoading ||
        activityFailed ||
        logDates.length > 0 ||
        chatDates.length > 0) && (
        <IdentityPanel
          title="Activity by day"
          loading={activityLoading}
          loadingVariant="block"
          error={activityFailed && chartEmpty}
          refreshFailed={activityFailed && !chartEmpty}
          onRetry={retryActivity}
        >
          <div className="px-4 py-4">
            <DailyActivityChart
              from={from}
              to={to}
              series={[
                { key: "changes", label: "Changes", dates: logDates },
                { key: "sessions", label: "Sessions", dates: chatDates },
              ]}
            />
          </div>
        </IdentityPanel>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <IdentityPanel
          title="Audit trail"
          handoffLabel="Audit Logs"
          handoffHref={handoffs.auditLogs}
          loading={auditQuery.isLoading}
          loadingRows={RECENT_ROWS}
          error={auditBlocked}
          refreshFailed={auditQuery.isError && !auditBlocked}
          onRetry={retryFailed(auditQuery)}
          footer={
            auditQuery.isError ? (
              <RefreshFailed onRetry={() => void auditQuery.refetch()} />
            ) : // Audit logs key on the Speakeasy user id, so a subject with no
            // directory row has nothing here even when it has telemetry.
            identity.userIds.length === 0 ? (
              "This identity resolves to no Speakeasy user, so no change is recorded under it."
            ) : (
              `Actor filtered to ${identity.displayName}`
            )
          }
        >
          {logs.length === 0 ? (
            <IdentityPanelEmpty>
              No recorded changes by this identity.
            </IdentityPanelEmpty>
          ) : (
            logs.slice(0, RECENT_ROWS).map((log) => (
              <IdentityPanelRow
                key={log.id}
                title={log.action}
                detail={[
                  log.subjectDisplayName ?? log.subjectType,
                  log.projectSlug,
                  // The API sends "unknown" when no surface was identified;
                  // printing that word says less than saying nothing.
                  log.actingSurface === "unknown" ? "" : log.actingSurface,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                trailing={<HumanizeDateTime date={log.createdAt} />}
              />
            ))
          )}
        </IdentityPanel>

        <IdentityPanel
          title="Chat sessions"
          handoffLabel="Agent Sessions"
          handoffHref={handoffs.agentSessions}
          loading={chatsQuery.isLoading}
          loadingRows={RECENT_ROWS}
          error={chatsBlocked}
          refreshFailed={chatsQuery.isError && !chatsBlocked}
          onRetry={retryFailed(chatsQuery)}
          footer={
            chatsQuery.isError ? (
              <RefreshFailed onRetry={() => void chatsQuery.refetch()} />
            ) : chatsQuery.data ? (
              `${chatsQuery.data.total ?? chats.length} session${
                (chatsQuery.data.total ?? chats.length) === 1 ? "" : "s"
              } this period`
            ) : undefined
          }
        >
          {!canReadOthersChats ? (
            <IdentityPanelEmpty>
              Listing someone else&rsquo;s sessions needs the chat:read
              permission.
            </IdentityPanelEmpty>
          ) : chats.length === 0 ? (
            <IdentityPanelEmpty>
              No chat sessions in this window.
            </IdentityPanelEmpty>
          ) : (
            chats
              .slice(0, RECENT_ROWS)
              .map((chat) => (
                <IdentityPanelRow
                  key={chat.id}
                  title={chat.title || "Untitled chat"}
                  detail={
                    chat.lastMessageTimestamp ? (
                      <HumanizeDateTime date={chat.lastMessageTimestamp} />
                    ) : undefined
                  }
                  trailing={`${chat.numMessages ?? 0} msgs`}
                />
              ))
          )}
        </IdentityPanel>
      </div>
    </IdentitySection>
  );
}
